import { useState } from 'react';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { useDebounce } from '@/shared/hooks/useDebounce';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import type { AuditLogEntry } from '../services/auditLogApi';

const ACTION_VARIANT: Record<string, 'default' | 'destructive' | 'outline' | 'secondary'> = {
  Created: 'default',
  Updated: 'secondary',
  Deleted: 'destructive',
};

const actionBadgeVariant = (action: string): 'default' | 'destructive' | 'outline' | 'secondary' =>
  ACTION_VARIANT[action] ?? 'outline';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const JsonExpander = ({ value, label }: { value: string | null; label: string }) => {
  const [open, setOpen] = useState(false);

  if (!value) return <span className="text-muted-foreground text-xs">—</span>;

  let parsed: Record<string, unknown> | null = null;
  try { parsed = JSON.parse(value); } catch { /* raw string */ }

  if (!parsed) {
    return <span className="text-xs font-mono text-muted-foreground">{value}</span>;
  }

  const keys = Object.keys(parsed);
  const summary = keys.slice(0, 2).map(k => `${k}: ${parsed![k]}`).join(', ') + (keys.length > 2 ? ` +${keys.length - 2}` : '');

  return (
    <div className="text-xs">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-left hover:text-foreground text-muted-foreground transition-colors"
        aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
      >
        {open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
        <span className="font-mono">{open ? '' : summary}</span>
      </button>
      {open && (
        <pre className="mt-1 p-2 rounded bg-muted/60 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all max-w-xs">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )}
    </div>
  );
};

const AuditLogRow = ({ entry }: { entry: AuditLogEntry }) => (
  <TableRow className="align-top">
    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
      {formatDate(entry.changedAt)}
    </TableCell>
    <TableCell className="font-medium text-sm">{entry.entityName}</TableCell>
    <TableCell className="text-xs font-mono text-muted-foreground">{entry.entityId}</TableCell>
    <TableCell>
      <Badge variant={actionBadgeVariant(entry.action)}>{entry.action}</Badge>
    </TableCell>
    <TableCell className="text-sm">{entry.changedBy ?? '—'}</TableCell>
    <TableCell>
      <JsonExpander value={entry.oldValues} label="old values" />
    </TableCell>
    <TableCell>
      <JsonExpander value={entry.newValues} label="new values" />
    </TableCell>
    <TableCell className="text-xs text-muted-foreground">{entry.ipAddress ?? '—'}</TableCell>
  </TableRow>
);

export const AuditLogsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data, isLoading, error } = useAuditLogs({
    search: debouncedSearch.trim() || undefined,
    pageNumber: currentPage,
    pageSize,
  });

  if (isLoading) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load audit logs</p>
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Audit Log</h1>
        <p className="text-muted-foreground mt-1">
          System-wide activity trail — read only
        </p>
      </div>

      <div className="relative w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search entity, action, user…"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Timestamp</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Entity ID</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Changed By</TableHead>
              <TableHead>Old Values</TableHead>
              <TableHead>New Values</TableHead>
              <TableHead>IP Address</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                  No audit log entries found
                </TableCell>
              </TableRow>
            ) : (
              items.map((entry) => <AuditLogRow key={entry.id} entry={entry} />)
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        currentPage={currentPage}
        pageSize={pageSize}
        totalCount={data?.totalCount ?? 0}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />
    </div>
  );
};
